import { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDocFromServer } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import type { LegalDocument } from './legalTypes';

export const LegalDocumentPreviewContext = createContext<LegalDocument | null>(null);

export async function loadLegalDocument(id?: string): Promise<LegalDocument> {
  const documentId = id ?? (await getDocFromServer(doc(db, 'legalConfig', 'current'))).data()?.documentId;
  if (typeof documentId !== 'string' || !/^\d{10}$/.test(documentId)) throw new Error('Legal document unavailable');
  const value = (await getDocFromServer(doc(db, 'legalDocuments', documentId))).data();
  if (!value || value.id !== documentId || value.schemaVersion !== 1 || value.acceptancePolicy !== 'next_login'
    || !value.terms?.html || !value.privacy?.html
    || !['login', 'create', 'login_email', 'create_email', 'claim'].every(key =>
      typeof value.notices?.[key] === 'string' && value.notices[key].includes('{terms}') && value.notices[key].includes('{privacy}'))) {
    throw new Error('Unsupported legal document');
  }
  return value as LegalDocument;
}

/** One snapshot per open screen: never swap the document underneath a button tap. */
export function useLegalDocument(enabled = true, id?: string) {
  const preview = useContext(LegalDocumentPreviewContext);
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    let active = true;
    setDocument(null);
    setError(false);
    if (enabled && !preview) loadLegalDocument(id).then(value => {
      if (active) setDocument(value);
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [enabled, id, retryKey, preview]);
  return { document: enabled ? preview ?? document : null, error: preview ? false : error, retry: () => setRetryKey(n => n + 1) };
}
